import { decideTopWindowAction } from '~/utils/topWindowGate'

/**
 * Refuses to open a page inside a frame. Attached per page with
 * `definePageMeta({ middleware: 'require-top-window' })`, and today only on
 * the Moodle connection approval page (pages/moodle/connect/[registrationId].vue),
 * whose only safety check is a person reading the site address before pressing
 * Connect. A framing site could hide that address under a decoy.
 *
 * This is one of TWO layers. Keep both:
 *
 * 1. A response header, `Content-Security-Policy: frame-ancestors 'none'` plus
 *    `X-Frame-Options: DENY`, on `/moodle/connect/**`. Firebase Hosting
 *    (production) sends it from firebase.json; the Nitro server (the Railway
 *    preview, `nuxt dev`) sends it from `routeRules` in nuxt.config.ts. Where
 *    it is sent, the browser enforces it before any of the page loads, so
 *    nothing in the page can race or undo it. But it covers only a real HTTP
 *    load of the lowercase path: both sources match paths case-sensitively
 *    while the router does not, and it is absent from any deploy that sends
 *    neither.
 * 2. This middleware. It runs on every navigation to the route: a
 *    client-side navigation (router push, no HTTP response at all) from a
 *    page that is legitimately framed (the LTI pages, /simulation/, /test/
 *    and /submission/ run inside an LMS or customer iframe, and the app is
 *    one SPA), a mixed-case path such as /MOODLE/CONNECT/<id>, and any deploy
 *    without the header. It is the only thing covering those cases. It runs
 *    in the page's own JavaScript, so it is the weaker layer where both apply.
 *
 * See docs/domains/moodle.md ("The page cannot be framed").
 *
 * It runs before the page component, so nothing clickable exists before the
 * decision. It never tries to break out of the frame or navigate the parent;
 * it sends the framed view to a page that explains what to do instead.
 */
export default defineNuxtRouteMiddleware(() => {
  const decision = decideTopWindowAction({
    isClient: import.meta.client,
    win: import.meta.client ? window : ({} as Window),
  })

  if (decision.type === 'refuse') {
    return navigateTo({ name: 'moodle-open-directly' }, { replace: true })
  }
})
